import { type FC, useState } from "react";

import { UnstyledButton } from "@mantine/core";
import { ExternalLinkIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { type AssistantChatSourceModel } from "../model";
import { getSourceLabel } from "../utils";

import styles from "./AssistantChatSources.module.css";
import { messages } from "./messages.generated";

interface Props {
    sources: AssistantChatSourceModel[];
}

const MAX_SOURCES_VISIBLE = 5;
const COLLAPSED_COUNT = 3;
const SOURCE_ICON_SIZE = 12;

const isSafeSourceLink = (link: string): boolean => {
    try {
        return new URL(link).protocol === "https:";
    } catch {
        return false;
    }
};

export const AssistantChatSources: FC<Props> = ({ sources }) => {
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const topSources = sources
        .filter(({ link }) => isSafeSourceLink(link))
        .sort((a, b) => b.relevanceScore - a.relevanceScore)
        .slice(0, MAX_SOURCES_VISIBLE);

    if (topSources.length === 0) return null;

    const collapsed = !expanded && topSources.length > COLLAPSED_COUNT;
    const visibleSources = collapsed ? topSources.slice(0, COLLAPSED_COUNT) : topSources;
    const hiddenCount = topSources.length - COLLAPSED_COUNT;

    return (
        <div className={styles.wrapper}>
            {visibleSources.map((source) => (
                <a key={source.id} className={styles.pill} href={source.link} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon size={SOURCE_ICON_SIZE} className={styles.icon} />
                    <span className={styles.pillLabel}>{getSourceLabel(source.link)}</span>
                </a>
            ))}
            {collapsed && (
                <UnstyledButton
                    className={styles.pill}
                    onClick={() => {
                        setExpanded(true);
                    }}
                    aria-label={intl.formatMessage(messages.sourcesMoreAriaLabel, { count: hiddenCount })}
                >
                    {`+${hiddenCount}`}
                </UnstyledButton>
            )}
        </div>
    );
};
